"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _classnames = _interopRequireDefault(require("classnames"));
var _react = _interopRequireWildcard(require("react"));
var _Field = _interopRequireDefault(require("../Field"));
var _ContextualMenu = _interopRequireDefault(require("../ContextualMenu"));
var _hooks = require("../../hooks");
var _CustomSelectDropdown = _interopRequireWildcard(require("./CustomSelectDropdown"));
require("./CustomSelect.scss");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/**
 * This is a [React](https://reactjs.org/) component that extends from the Vanilla [Select](https://vanillaframework.io/docs/base/forms#select) element.
 *
 * The aim of this component is to provide a select component with customisable options and a dropdown menu, whilst maintaining accessibility and usability.
 */
const CustomSelect = _ref => {
  let {
    value,
    options,
    onChange,
    onSearch,
    id,
    name,
    disabled,
    success,
    error,
    help,
    wrapperClassName,
    toggleClassName,
    dropdownClassName,
    searchable = "auto",
    takeFocus,
    header,
    selectRef,
    initialPosition = "left",
    ...fieldProps
  } = _ref;
  const [isOpen, setIsOpen] = (0, _react.useState)(false);
  const validationId = (0, _react.useId)();
  const defaultSelectId = (0, _react.useId)();
  const selectId = id || defaultSelectId;
  const helpId = (0, _react.useId)();
  const hasError = !!error;

  // Close the dropdown when the browser tab is hidden
  const onBrowserTabHidden = () => {
    if (document.visibilityState === "hidden") {
      setIsOpen(false);
    }
  };
  (0, _hooks.useListener)(window, onBrowserTabHidden, "visibilitychange");

  // Close the dropdown when the browser window loses focus
  (0, _hooks.useListener)(window, () => setIsOpen(false), "blur");
  (0, _react.useImperativeHandle)(selectRef, () => ({
    open: () => {
      var _document$getElementB;
      setIsOpen(true);
      (_document$getElementB = document.getElementById(selectId)) === null || _document$getElementB === void 0 || _document$getElementB.focus();
    },
    focus: () => {
      var _document$getElementB2;
      return (_document$getElementB2 = document.getElementById(selectId)) === null || _document$getElementB2 === void 0 ? void 0 : _document$getElementB2.focus();
    },
    close: setIsOpen.bind(null, false),
    isOpen: isOpen
  }), [isOpen, selectId]);
  (0, _react.useEffect)(() => {
    if (takeFocus) {
      const toggleButton = document.getElementById(selectId);
      toggleButton === null || toggleButton === void 0 || toggleButton.focus();
    }
  }, [takeFocus, selectId]);
  const selectedOption = options.find(option => option.value === value);
  const toggleLabel = /*#__PURE__*/_react.default.createElement("span", {
    className: "toggle-label u-truncate"
  }, selectedOption ? (0, _CustomSelectDropdown.getOptionText)(selectedOption) : "Select an option");
  const handleSelect = value => {
    var _document$getElementB3;
    (_document$getElementB3 = document.getElementById(selectId)) === null || _document$getElementB3 === void 0 || _document$getElementB3.focus();
    setIsOpen(false);
    onChange(value);
  };
  return /*#__PURE__*/_react.default.createElement(_Field.default, _extends({}, fieldProps, {
    className: (0, _classnames.default)("p-custom-select", wrapperClassName),
    error: error,
    forId: selectId,
    help: help,
    helpId: helpId,
    isSelect: true,
    success: success,
    validationId: validationId
  }), /*#__PURE__*/_react.default.createElement(_ContextualMenu.default, {
    "aria-describedby": [help ? helpId : null, success ? validationId : null].filter(Boolean).join(" "),
    "aria-errormessage": hasError ? validationId : undefined,
    "aria-invalid": hasError,
    toggleClassName: (0, _classnames.default)("p-custom-select__toggle", "p-form-validation__input", toggleClassName, {
      active: isOpen
    }),
    toggleLabel: toggleLabel,
    visible: isOpen,
    onToggleMenu: open => {
      // Handle syncing the state when toggling the menu from within the
      // contextual menu component e.g. when clicking outside.
      if (open !== isOpen) {
        setIsOpen(open);
      }
    },
    toggleProps: {
      id: selectId,
      disabled: disabled,
      // tabIndex is set to -1 when disabled to prevent keyboard navigation to the select toggle
      tabIndex: disabled ? -1 : 0
    },
    className: "p-custom-select__wrapper",
    dropdownClassName: dropdownClassName,
    style: {
      width: "100%"
    },
    autoAdjust: true,
    position: initialPosition
  }, close => /*#__PURE__*/_react.default.createElement(_CustomSelectDropdown.default, {
    searchable: searchable,
    onSearch: onSearch,
    name: name || "",
    options: options || [],
    onSelect: handleSelect,
    onClose: () => {
      var _document$getElementB4;
      // When pressing ESC to close the dropdown, we keep focus on the toggle button
      close();
      (_document$getElementB4 = document.getElementById(selectId)) === null || _document$getElementB4 === void 0 || _document$getElementB4.focus();
    },
    header: header,
    toggleId: selectId
  })));
};
var _default = exports.default = CustomSelect;